import Big from 'big.js'
import { Measure } from '../types'


enum FrequencyMeasurementSystem {
  siUnits = 'SI Units',
  commonNonSI = 'Common Non-SI Units',
  angularVelocity = 'Angular Velocity Units'
}

const frequencyUnits = {
  nanohertz: {
    displayName: 'Nanohertz',
    codeName: 'nanohertz',
    url: 'nanohertz',
    symbol: 'nHz',
    toBaseUnit: (value: Big) => value.times(1e-9),
    fromBaseUnit: (value: Big) => value.div(1e-9),
    system: FrequencyMeasurementSystem.siUnits
  },
  microhertz: {
    displayName: 'Microhertz',
    codeName: 'microhertz',
    url: 'microhertz',
    symbol: 'μHz',
    toBaseUnit: (value: Big) => value.times(1e-6),
    fromBaseUnit: (value: Big) => value.div(1e-6),
    system: FrequencyMeasurementSystem.siUnits
  },
  millihertz: {
    displayName: 'Millihertz',
    codeName: 'millihertz',
    url: 'millihertz',
    symbol: 'mHz',
    toBaseUnit: (value: Big) => value.times(1e-3),
    fromBaseUnit: (value: Big) => value.div(1e-3),
    system: FrequencyMeasurementSystem.siUnits
  },
  hertz: {
    displayName: 'Hertz',
    codeName: 'hertz',
    url: 'hertz',
    symbol: 'Hz',
    toBaseUnit: (value: Big) => value,
    fromBaseUnit: (value: Big) => value,
    system: FrequencyMeasurementSystem.siUnits
  },
  kilohertz: {
    displayName: 'Kilohertz',
    codeName: 'kilohertz',
    url: 'kilohertz',
    symbol: 'kHz',
    toBaseUnit: (value: Big) => value.times(1e3),
    fromBaseUnit: (value: Big) => value.div(1e3),
    system: FrequencyMeasurementSystem.siUnits
  },
  megahertz: {
    displayName: 'Megahertz',
    codeName: 'megahertz',
    url: 'megahertz',
    symbol: 'MHz',
    toBaseUnit: (value: Big) => value.times(1e6),
    fromBaseUnit: (value: Big) => value.div(1e6),
    system: FrequencyMeasurementSystem.siUnits
  },
  gigahertz: {
    displayName: 'Gigahertz',
    codeName: 'gigahertz',
    url: 'gigahertz',
    symbol: 'GHz',
    toBaseUnit: (value: Big) => value.times(1e9),
    fromBaseUnit: (value: Big) => value.div(1e9),
    system: FrequencyMeasurementSystem.siUnits
  },
  terahertz: {
    displayName: 'Terahertz',
    codeName: 'terahertz',
    url: 'terahertz',
    symbol: 'THz',
    toBaseUnit: (value: Big) => value.times(1e12),
    fromBaseUnit: (value: Big) => value.div(1e12),
    system: FrequencyMeasurementSystem.siUnits
  },


  cycle_per_second: {
    displayName: 'Cycle per second',
    codeName: 'cycle_per_second',
    url: 'cycle-per-second',
    symbol: 'cps',
    toBaseUnit: (value: Big) => value,
    fromBaseUnit: (value: Big) => value,
    system: FrequencyMeasurementSystem.commonNonSI
  },
  revolutions_per_minute: {
    displayName: 'Revolutions per minute',
    codeName: 'revolutions_per_minute',
    url: 'revolutions-per-minute',
    symbol: 'rpm',
    toBaseUnit: (value: Big) => value.div(60),
    fromBaseUnit: (value: Big) => value.times(60),
    system: FrequencyMeasurementSystem.commonNonSI
  },
  beats_per_minute: {
    displayName: 'Beats per minute',
    codeName: 'beats_per_minute',
    url: 'beats-per-minute',
    symbol: 'bpm',
    toBaseUnit: (value: Big) => value.div(60),
    fromBaseUnit: (value: Big) => value.times(60),
    system: FrequencyMeasurementSystem.commonNonSI
  },


  radian_per_second: {
    displayName: 'Radian per second',
    codeName: 'radian_per_second',
    url: 'radian-per-second',
    symbol: 'rad/s',
    toBaseUnit: (value: Big) => value.div(6.283185307179586),
    fromBaseUnit: (value: Big) => value.times(6.283185307179586),
    system: FrequencyMeasurementSystem.angularVelocity
  },
  radian_per_minute: {
    displayName: 'Radian per minute',
    codeName: 'radian_per_minute',
    url: 'radian-per-minute',
    symbol: 'rad/min',
    toBaseUnit: (value: Big) => value.div(6.283185307179586).div(60),
    fromBaseUnit: (value: Big) => value.times(6.283185307179586).times(60),
    system: FrequencyMeasurementSystem.angularVelocity
  },
  radian_per_hour: {
    displayName: 'Radian per hour',
    codeName: 'radian_per_hour',
    url: 'radian-per-hour',
    symbol: 'rad/hr',
    toBaseUnit: (value: Big) => value.div(6.283185307179586).div(3600),
    fromBaseUnit: (value: Big) => value.times(6.283185307179586).times(3600),
    system: FrequencyMeasurementSystem.angularVelocity
  },
  degrees_per_second: {
    displayName: 'Degrees per second',
    codeName: 'degrees_per_second',
    url: 'degrees-per-second',
    symbol: '°/s',
    toBaseUnit: (value: Big) => value.div(360),
    fromBaseUnit: (value: Big) => value.times(360),
    system: FrequencyMeasurementSystem.angularVelocity
  },
  degrees_per_minute: {
    displayName: 'Degrees per minute',
    codeName: 'degrees_per_minute',
    url: 'degrees-per-minute',
    symbol: '°/min',
    toBaseUnit: (value: Big) => value.div(21600),
    fromBaseUnit: (value: Big) => value.times(21600),
    system: FrequencyMeasurementSystem.angularVelocity
  },
  degrees_per_hour: {
    displayName: 'Degrees per hour',
    codeName: 'degrees_per_hour',
    url: 'degrees-per-hour',
    symbol: '°/h',
    toBaseUnit: (value: Big) => value.div(1296000),
    fromBaseUnit: (value: Big) => value.times(1296000),
    system: FrequencyMeasurementSystem.angularVelocity
  }
}

export const frequency: Measure = {
  displayName: 'Frequency',
  codeName: 'frequency',
  url: 'frequency',
  units: frequencyUnits,
  baseUnit: frequencyUnits.hertz,
  defaultFromUnit: frequencyUnits.megahertz,
  defaultToUnit: frequencyUnits.hertz
}
